import { useMemo, useState } from "react";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@semo/shared-ui";
import { AlertTriangle } from "@semo/icons";

/**
 * paperCode=135 (수의계약체결제한여부확인서)
 *
 * 「공직자의 이해충돌 방지법」제12조 수의계약 체결 제한 확인.
 * payload 는 `documentInputs.contractRestrictionConfirm.answers` 8개("YES" | "NO").
 *
 * UI 정책 (2026-08-03 개정):
 *   - 8개 문항 모두 "아니오" 로 **선택된 상태로 시작**한다. 해당되는 항목만 "예"로 바꾸면 된다.
 *   - 응답값(YES/NO)은 서류 발급/제출 여부의 조건이 아니다. [제출] 버튼은 응답값과 무관하게
 *     상시 활성화되며, 선택값은 백엔드로 그대로 전달되어 확인서에 기재된다.
 *   - "예" 가 하나라도 있으면 [제출] 위에 안내(help text) 를 노출하고, 백엔드가 발주기관에
 *     경고 알림을 보낸다. 판단·조치는 발주기관 몫.
 */

type Answer = "NO" | "YES";

interface Question {
  /** 화면 렌더 안정성을 위한 고정 인덱스 (0..7, 답변 배열 순서와 1:1). */
  index: number;
  label: string;
}

interface Section {
  title: string;
  questions: Question[];
}

// 문항 순서·문구는 c-market 서식 「별지 제14호」 원문 기준. 인덱스는 answers[i] 매핑용.
const SECTIONS: readonly Section[] = [
  {
    title: "공직자 관계",
    questions: [
      {
        index: 0,
        label:
          "발주기관의 소속 고위공직자의 배우자, 직계 존ㆍ비속 또는 특수관계에 있는 자입니까?",
      },
      {
        index: 1,
        label: "계약 업무 담당 공직자의 배우자 또는 존ㆍ비속입니까?",
      },
    ],
  },
  {
    title: "퇴직공직자ㆍ이해관계",
    questions: [
      {
        index: 2,
        label:
          "발주기관 소속 퇴직공직자로서 퇴직 전 2년간 해당 기관과 직접 관련 업무를 담당한 자가 운영하는 기업입니까?",
      },
      {
        index: 3,
        label: "발주기관 소속 퇴직공직자가 퇴직 후 2년 이내에 취업한 기업입니까?",
      },
      {
        index: 4,
        label:
          "발주기관과 금전거래 등 사적 이해관계가 있는 자가 운영하는 기업입니까?",
      },
    ],
  },
  {
    title: "계약ㆍ세금 이력",
    questions: [
      {
        index: 5,
        label:
          "최근 2년 이내 발주기관과의 계약에서 부정당업자 제재를 받은 사실이 있습니까?",
      },
      {
        index: 6,
        label:
          "최근 1년 이내 발주기관과의 계약에서 계약 불이행으로 인한 계약 해지 사실이 있습니까?",
      },
      {
        index: 7,
        label: "국세, 지방세 등 관련 세금 체납 사실이 있습니까?",
      },
    ],
  },
] as const;

const TOTAL_QUESTIONS = 8;

/**
 * 문항 라벨을 인덱스 순(0..7)으로 평탄화한 배열.
 * 카드의 경고 배너가 flaggedIndexes → 문항 문구를 되짚는 데 쓴다.
 */
export const CONTRACT_RESTRICTION_QUESTIONS: readonly string[] = SECTIONS.flatMap(
  (section) => section.questions,
)
  .slice()
  .sort((a, b) => a.index - b.index)
  .map((q) => q.label);

const DISCLAIMER =
  "「공직자의 이해충돌 방지법」제12조에 따른 수의계약 체결 제한 해당 여부 확인. " +
  "선택한 내용이 [확인서]로 자동 발급되며, 직접 작성한 것과 동일한 효력을 갖습니다.";

const YES_HELP_TEXT =
  "'예'로 선택한 항목이 있어 계약이 불가할 수 있어요. 해당 물품이 단독 생산품인 경우, 수의계약이 허용될 수 있습니다.";

interface ContractRestrictionAllNoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (answers: string[]) => Promise<void>;
}

export function ContractRestrictionAllNoModal({
  open,
  onOpenChange,
  onSubmit,
}: ContractRestrictionAllNoModalProps) {
  // 기본값: 전 문항 "아니오". 해당되는 항목만 "예"로 바꾸면 된다.
  const [answers, setAnswers] = useState<Answer[]>(() =>
    Array<Answer>(TOTAL_QUESTIONS).fill("NO"),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const yesCount = useMemo(
    () => answers.filter((a) => a === "YES").length,
    [answers],
  );
  // 응답값과 무관하게 상시 활성화. 서류 발급/제출의 조건은 응답값이 아니다.
  const canSubmit = !isSubmitting;

  const setAnswer = (index: number, value: Answer) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const resetAndClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setAnswers(Array<Answer>(TOTAL_QUESTIONS).fill("NO"));
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      // 선택값을 그대로 전달한다. "YES" 가 있으면 백엔드가 발주기관에 경고를 통지한다.
      await onSubmit([...answers]);
      resetAndClose(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            수의계약체결제한여부 확인
          </DialogTitle>
          <DialogDescription>
            전 문항이 <b>&quot;아니오&quot;</b>로 선택되어 있습니다. 해당되는
            항목이 있으면 <b>&quot;예&quot;</b>로 바꾼 뒤 제출해 주세요.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-5 pr-1">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h4 className="text-xs font-semibold text-slate-600 mb-2">
                {section.title}
              </h4>
              <ul className="space-y-2">
                {section.questions.map((q) => (
                  <QuestionRow
                    key={q.index}
                    label={q.label}
                    value={answers[q.index] ?? "NO"}
                    onChange={(v) => setAnswer(q.index, v)}
                  />
                ))}
              </ul>
            </section>
          ))}

          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-3 text-xs leading-relaxed text-slate-700">
            {DISCLAIMER}
          </div>
        </div>

        {yesCount > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs leading-relaxed text-amber-800">
              {YES_HELP_TEXT}
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 pt-3">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => resetAndClose(false)}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isSubmitting ? "제출 중..." : "제출"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------- sub-parts -------------------------------- */

function QuestionRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Answer;
  onChange: (v: Answer) => void;
}) {
  const isYes = value === "YES";
  return (
    <li
      className={cn(
        "rounded-lg border px-3 py-2.5",
        isYes ? "border-amber-300 bg-amber-50/50" : "border-slate-200 bg-white",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm leading-relaxed text-slate-800 flex-1">
          {label}
        </p>
        <div className="flex items-center gap-1 shrink-0">
          <ChoiceButton
            active={value === "NO"}
            variant="no"
            onClick={() => onChange("NO")}
          >
            아니오
          </ChoiceButton>
          <ChoiceButton
            active={value === "YES"}
            variant="yes"
            onClick={() => onChange("YES")}
          >
            예
          </ChoiceButton>
        </div>
      </div>
      {isYes && (
        <p className="mt-1.5 text-2xs font-semibold text-amber-700">
          이 항목은 확인서에 &quot;예&quot;로 기재되며 발주기관에 통보됩니다.
        </p>
      )}
    </li>
  );
}

function ChoiceButton({
  active,
  variant,
  onClick,
  children,
}: {
  active: boolean;
  variant: "no" | "yes";
  onClick: () => void;
  children: React.ReactNode;
}) {
  const activeCls =
    variant === "no"
      ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
      : "bg-amber-500 text-white border-amber-500 hover:bg-amber-600";
  const inactiveCls =
    "bg-white text-slate-600 border-slate-300 hover:bg-slate-50";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-7 min-w-[52px] px-3 rounded-md border text-xs font-semibold transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
        active ? activeCls : inactiveCls,
      )}
    >
      {children}
    </button>
  );
}
